/**
 * OrgSettingsContext — Provides organisation-wide settings (currency, date format, etc.)
 * to all components. Fetches once on mount, exposes a refresh function.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const OrgSettingsContext = createContext({});

const DEFAULTS = {
  currency: 'INR',
  currencySymbol: '₹',
  currencyLocale: 'en-IN',
  dateFormat: 'DD/MM/YYYY',
  timezone: 'Asia/Kolkata',
  orgName: 'ICT OPS',
  fiscalYearStart: 4,
};

export function OrgSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/settings');
      setSettings({ ...DEFAULTS, ...data });
    } catch {
      // Keep defaults on failure
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  return (
    <OrgSettingsContext.Provider value={{ ...settings, loaded, refresh: fetchSettings }}>
      {children}
    </OrgSettingsContext.Provider>
  );
}

export function useOrgSettings() {
  return useContext(OrgSettingsContext);
}

/**
 * Format a numeric value as currency using the org settings.
 * Usage: formatCurrency(12500, settings) → "₹12,500"
 */
export function formatCurrency(amount, settings = DEFAULTS) {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(settings.currencyLocale || 'en-IN', {
      style: 'currency',
      currency: settings.currency || 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${settings.currencySymbol || '₹'}${Number(amount).toLocaleString()}`;
  }
}

/**
 * Short format for large numbers: ₹12.5k, ₹1.2M
 */
export function formatCurrencyShort(amount, settings = DEFAULTS) {
  if (amount == null) return '—';
  const symbol = settings.currencySymbol || '₹';
  const abs = Math.abs(amount);
  if (abs >= 10000000) return `${symbol}${(amount / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000) return `${symbol}${(amount / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `${symbol}${(amount / 1000).toFixed(0)}k`;
  return `${symbol}${amount}`;
}

/**
 * Format a date value using the org timezone setting.
 * Returns date only (e.g. "07/07/2026" or "07 Jul 2026").
 * Usage: formatDate(someDate, settings)
 */
export function formatDate(date, settings = DEFAULTS) {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat(settings.currencyLocale || 'en-IN', {
      timeZone: settings.timezone || 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(date));
  } catch {
    return new Date(date).toLocaleDateString();
  }
}

/**
 * Format a date+time value using the org timezone setting.
 * Returns date and time (e.g. "07 Jul 2026, 5:30 PM").
 * Usage: formatDateTime(someDate, settings)
 */
export function formatDateTime(date, settings = DEFAULTS) {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat(settings.currencyLocale || 'en-IN', {
      timeZone: settings.timezone || 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(date));
  } catch {
    return new Date(date).toLocaleString();
  }
}
